import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronLeft, Copy, Maximize2 } from 'lucide-react'
import { KnowledgeAskCitation, KnowledgeDocumentListItem } from '@digital-twins/shared-types'

import { reconcileArtifactBody } from '@/components/artifact-editor/reconcileArtifactBody'
import {
  StudyDocPlotLookup,
  StudyDocPrintButton,
  StudyDocQuote,
  STUDY_DOC_READING_COLUMN_CLASS,
} from '@/components/study-doc'
import { StudyDocDecisionConfidence } from '@/components/study-doc/StudyDocDecisionMasthead/StudyDocDecisionMasthead'
import { STUDY_DOC_CARD_PAD, studyDocCardPadStyle } from '@/components/study-doc/StudyDocMagazineBand/StudyDocMagazineBand'
import { StudyDocStripLookup } from '@/components/study-doc/StudyDocMagazineBand/resolveMagazineBandSource'
import { Text } from '@/components/ui/Text'
import { ThreadAssetView } from '@/lib/api'

import { artifactDisplayLabel, bodyOpensWithHeading, localizeArtifactCategory } from './artifactDisplayLabel'
import { ThreadArtifactItem } from './collectThreadArtifacts'
import { ThreadArtifactBodyPreview } from './ThreadArtifactBodyPreview'
import { useThreadArtifactTips } from './useThreadArtifactTips'

/**
 * Full-body takeover of the drawer for one artifact.
 *
 * The inline stack and the rail's Artifacts list both show peeks; expanding
 * either one swaps the whole drawer to this view. Questions payloads render
 * as read-only cards; prose uses `MarkdownBody` at document density. Mirrors
 * `ThreadSourcePanel`'s "back to the list" affordance on purpose: this
 * replaces the pane the same way a source tab does, so the reader has one
 * control to get back, not a close plus a separate back.
 *
 * Read-only. Editing lives on the collapsed card in the thread column; this
 * view is for reading, copying and printing. The back control names the
 * artifact's category only ("Report", "Desk research"): the body carries its
 * own title.
 *
 * The synthesized H1 is dropped when the body already opens with an ATX
 * heading, or when a category label exists. Otherwise it uses the extracted
 * headline, never the stored step title.
 */

export interface ThreadArtifactExpandedViewProps {
  item: ThreadArtifactItem
  /** Back to the outputs list. The drawer itself stays open. */
  onClose: () => void
  /** Opens the full-viewport surface (icons stay in the pane chrome). */
  onFullscreen?: () => void
  plots?: Record<string, StudyDocPlotLookup>
  quotes?: Record<string, StudyDocQuote>
  plotRegion?: 'all' | 'plot'
  /** Slim picker population for mosaic tiles missing a row image. */
  populationId?: string
  /** FactBundle strip lookup for `{{quotes_strip:…}}` full-bleed. */
  strips?: Record<string, StudyDocStripLookup>
  /** P(best) masthead. Absent → empty-stable, no masthead. */
  decisionConfidence?: StudyDocDecisionConfidence | null
  threadAssets?: Array<ThreadAssetView>
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  onOpenCitation?: (index: number, citation: KnowledgeAskCitation) => void
}

export function ThreadArtifactExpandedView(
  props: ThreadArtifactExpandedViewProps,
) {
  const {
    item,
    onClose,
    onFullscreen,
    plots,
    quotes,
    plotRegion,
    populationId,
    strips,
    decisionConfidence,
    threadAssets,
    docsByToken,
    onOpenCitation,
  } = props

  const { t } = useTranslation('app')

  const [hasCopied, setHasCopied] = useState(false)
  const { tipForItem } = useThreadArtifactTips(item.runToken)
  const tip = tipForItem(item)
  const displayBody = reconcileArtifactBody(item.body, tip)
  const label = artifactDisplayLabel({ ...item, body: displayBody })
  const categoryText = localizeArtifactCategory(label.category, label.letter)
  const accessibleName = categoryText
    ? `${categoryText}: ${label.headline}`
    : label.headline
  const showSynthesizedTitle =
    !bodyOpensWithHeading(displayBody) && label.category === null

  async function handleCopyClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (!navigator.clipboard?.writeText) {
      return
    }
    try {
      await navigator.clipboard.writeText(displayBody)
      setHasCopied(true)
      window.setTimeout(() => setHasCopied(false), 1500)
    } catch {
      setHasCopied(false)
    }
  }

  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      data-testid="thread-artifact-expanded"
    >
      <div className="flex items-center gap-1 px-2 py-1.5">
        <button
          type="button"
          className="inline-flex min-w-0 flex-1 items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          data-testid="thread-artifact-expanded-close"
          onClick={onClose}
        >
          <ChevronLeft className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <Text
            as="span"
            size="xs"
            className="min-w-0 max-w-[240px] truncate text-left leading-none"
          >
            <Text as="span" size="xs" weight="semibold" className="leading-none">
              {categoryText ?? label.headline}
            </Text>
          </Text>
        </button>
        <button
          type="button"
          className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          data-testid="thread-artifact-expanded-copy"
          aria-label={
            hasCopied
              ? t('threads.detail.rail.copiedNamedAria', { name: accessibleName })
              : t('threads.detail.rail.copyNamedAria', { name: accessibleName })
          }
          onClick={handleCopyClick}
        >
          {hasCopied ? (
            <Check className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
        <StudyDocPrintButton
          accessibleName={accessibleName}
        />
        {onFullscreen ? (
          <button
            type="button"
            className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            data-testid="thread-artifact-expanded-fullscreen"
            aria-label={t('threads.detail.rail.expandFullscreenAria', {
              name: accessibleName,
            })}
            onClick={onFullscreen}
          >
            <Maximize2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto px-5 py-4"
        style={studyDocCardPadStyle(STUDY_DOC_CARD_PAD.rail)}
        data-study-doc-print-root=""
      >
        <div
          className={STUDY_DOC_READING_COLUMN_CLASS}
          data-testid="thread-artifact-expanded-column"
        >
        {showSynthesizedTitle ? (
          <h1
            className="mb-2 mt-0 font-display text-2xl font-medium leading-tight text-foreground"
            data-testid="thread-artifact-expanded-title"
          >
            {label.headline}
          </h1>
        ) : null}
        <ThreadArtifactBodyPreview
          body={displayBody}
          plots={plots}
          quotes={quotes}
          plotRegion={plotRegion}
          populationId={populationId}
          strips={strips}
          decisionConfidence={decisionConfidence}
          threadAssets={threadAssets}
          docsByToken={docsByToken}
          onOpenCitation={onOpenCitation}
          runToken={item.runToken}
        />
        </div>
      </div>
    </div>
  )
}
